// Renders the subtype-specific editing UI inside the inline section editor.
// For prose / callouts uses RichTextEditor. For media/data uses simple field
// editors (URL inputs, table cell inputs, graph rows).
import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import RichTextEditor from "@/components/RichTextEditor";
import { Plus, Trash2, UploadCloud, ArrowUp, ArrowDown, GripVertical, Image as ImageIcon, Type as TypeIcon, MousePointerClick } from "lucide-react";
import { toast } from "sonner";
import DropZone from "@/components/DropZone";
import { uploadImage } from "@/lib/imageUpload";
import ImageStyleFields, { BackgroundImageStyleFields } from "@/components/imageStyleFields";
import { LAYOUT_GRID_PRESETS, defaultLayoutGridCell, makeDefaultCells } from "./sectionTypes";
import { Button } from "@/components/ui/button";

// Common styling controls shown on every dynamic subtype's editor.
// Sets background color, background image, and vertical padding on the section wrapper.
// Hoisted to module scope so both SectionSubtypeEditor and LayoutGridEditor can reach it.
function DynamicSectionStyle({ d, onPatch }) {
  const uploadBg = async (files) => {
    try {
      const data = await uploadImage(files[0]);
      onPatch({ background_image: data.url });
      toast.success("Background uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Upload failed");
    }
  };
  return (
    <div className="grid grid-cols-2 gap-3 pt-3 mt-3 border-t border-slate-200">
      <div className="col-span-2 text-xs uppercase tracking-widest text-slate-500 font-semibold">Background & spacing</div>
      <div className="col-span-2">
        <ThemedColorField label="Background color"
          value={d.background_color} valueDark={d.background_color_dark}
          onChange={(v) => onPatch({ background_color: v })}
          onChangeDark={(v) => onPatch({ background_color_dark: v })}
          defaultColor="#ffffff"/>
      </div>
      <div>
        <label className="text-xs uppercase tracking-widest text-slate-500">Vertical padding</label>
        <Select value={d.padding_y || "md"} onValueChange={v => onPatch({ padding_y: v })}>
          <SelectTrigger><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">None</SelectItem>
            <SelectItem value="sm">Small</SelectItem>
            <SelectItem value="md">Medium</SelectItem>
            <SelectItem value="lg">Large</SelectItem>
            <SelectItem value="xl">Extra large</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="col-span-2">
        <label className="text-xs uppercase tracking-widest text-slate-500">Background image</label>
        <DropZone onFiles={uploadBg} className="rounded-md">
          <div className="border border-dashed border-slate-300 rounded-md p-3 bg-slate-50/50 flex items-center gap-3">
            {d.background_image ? (
              <img src={d.background_image} alt="" className="w-24 h-16 object-cover rounded shrink-0"/>
            ) : (
              <div className="w-24 h-16 grid place-items-center text-slate-400 shrink-0"><UploadCloud className="w-5 h-5"/></div>
            )}
            <div className="flex-1 min-w-0">
              <Input value={d.background_image || ""} placeholder="Drop an image or paste a URL"
                     onChange={e => onPatch({ background_image: e.target.value })}/>
              <div className="flex items-center gap-3 mt-1">
                <label className="text-xs text-sky-700 hover:text-sky-900 cursor-pointer">
                  Choose file
                  <input type="file" accept="image/*" className="sr-only"
                         onChange={e => e.target.files?.[0] && uploadBg([e.target.files[0]])}/>
                </label>
                {d.background_image && (
                  <button onClick={() => onPatch({ background_image: "" })} className="text-xs text-slate-400 hover:text-red-600">remove</button>
                )}
              </div>
            </div>
          </div>
        </DropZone>
        {d.background_image && <BackgroundImageStyleFields d={d} patch={onPatch}/>}
      </div>
    </div>
  );
}

const setData = (section, patch) => ({ ...section, data: { ...(section.data || {}), ...patch } });

// Small pair of color inputs — one for light mode, one for dark. Saves as
// `{key}` and `{key}_dark` on the given data object. Used by every section
// that offers custom colors so admins can theme both.
function ThemedColorField({ label, value, valueDark, onChange, onChangeDark, defaultColor = "#ffffff" }) {
  return (
    <div className="mt-2">
      <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">{label} <span className="text-slate-400 normal-case">— per theme</span></div>
      <div className="grid grid-cols-2 gap-2">
        <div className="flex items-center gap-2 border border-slate-200 rounded px-2 py-1">
          <span className="text-[10px] uppercase tracking-widest text-slate-500 w-10 shrink-0">Light</span>
          <input type="color" value={value || defaultColor} onChange={(e) => onChange(e.target.value)}
                 className="w-9 h-7 rounded border border-slate-300 cursor-pointer"/>
          <Input value={value || ""} placeholder="default"
                 onChange={(e) => onChange(e.target.value)} className="h-7 text-xs"/>
          {value && <button type="button" onClick={() => onChange("")} className="text-[10px] text-slate-400 hover:text-red-600 shrink-0">clear</button>}
        </div>
        <div className="flex items-center gap-2 border border-slate-800 bg-slate-900 rounded px-2 py-1">
          <span className="text-[10px] uppercase tracking-widest text-slate-400 w-10 shrink-0">Dark</span>
          <input type="color" value={valueDark || defaultColor} onChange={(e) => onChangeDark(e.target.value)}
                 className="w-9 h-7 rounded border border-slate-700 cursor-pointer"/>
          <Input value={valueDark || ""} placeholder="default"
                 onChange={(e) => onChangeDark(e.target.value)} className="h-7 text-xs bg-slate-800 border-slate-700 text-slate-100"/>
          {valueDark && <button type="button" onClick={() => onChangeDark("")} className="text-[10px] text-slate-400 hover:text-red-400 shrink-0">clear</button>}
        </div>
      </div>
    </div>
  );
}

export default function SectionSubtypeEditor({ section, onChange, confirmDelete }) {
  const s = { ...section, subtype: section?.subtype || "prose" };
  const d = s.data || {};
  // Fallback if no confirm helper was supplied — just do it immediately.
  const confirm = confirmDelete || ((_t, _b, fn) => fn?.());

  if (s.subtype === "tip_jar") {
    const setD = (patch) => onChange(setData(s, patch));
    const amts = Array.isArray(d.amounts_cents) && d.amounts_cents.length ? d.amounts_cents : [300, 500, 1000];
    const updateAmount = (i, dollarsStr) => {
      const n = parseFloat(dollarsStr);
      const list = [...amts];
      list[i] = Number.isFinite(n) ? Math.round(n * 100) : 0;
      setD({ amounts_cents: list.filter(v => v > 0) });
    };
    const removeAmount = (i) => setD({ amounts_cents: amts.filter((_, idx) => idx !== i) });
    const addAmount = () => setD({ amounts_cents: [...amts, 1500].slice(0, 5) });
    return (
      <div className="space-y-3" data-testid="tip-jar-editor">
        <div>
          <label className="text-xs uppercase tracking-widest text-slate-500 block mb-1">Heading</label>
          <Input
            data-testid="tip-jar-editor-heading"
            value={d.heading || ""}
            onChange={e => setD({ heading: e.target.value.slice(0, 80) })}
            placeholder="Enjoying Hospice Synopsis?"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-slate-500 block mb-1">Body</label>
          <textarea
            data-testid="tip-jar-editor-body"
            value={d.body || ""}
            onChange={e => setD({ body: e.target.value.slice(0, 280) })}
            rows={3}
            className="w-full border border-slate-300 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-600"
            placeholder="Short pitch, one or two sentences."
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500 block mb-1">Button label</label>
            <Input
              data-testid="tip-jar-editor-button-label"
              value={d.button_label || ""}
              onChange={e => setD({ button_label: e.target.value.slice(0, 32) })}
              placeholder="Buy me a coffee"
            />
          </div>
          <div className="flex items-end">
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                data-testid="tip-jar-editor-allow-custom"
                checked={d.allow_custom !== false}
                onChange={e => setD({ allow_custom: e.target.checked })}
              />
              Allow custom amount
            </label>
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-slate-500 block mb-1">
            Amount presets <span className="normal-case text-slate-400">(in dollars, up to 5)</span>
          </label>
          <div className="flex flex-wrap items-center gap-2" data-testid="tip-jar-editor-amounts">
            {amts.map((c, i) => (
              <div key={i} className="flex items-center gap-1">
                <span className="text-slate-500 text-sm">$</span>
                <Input
                  data-testid={`tip-jar-editor-amount-${i}`}
                  type="number"
                  min="1"
                  max="500"
                  step="1"
                  value={(c / 100).toString()}
                  onChange={e => updateAmount(i, e.target.value)}
                  className="w-20"
                />
                {amts.length > 1 && (
                  <button
                    type="button"
                    data-testid={`tip-jar-editor-amount-remove-${i}`}
                    onClick={() => removeAmount(i)}
                    className="text-xs text-slate-400 hover:text-red-600"
                    aria-label="Remove amount"
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
            {amts.length < 5 && (
              <button
                type="button"
                data-testid="tip-jar-editor-amount-add"
                onClick={addAmount}
                className="text-xs px-2 py-1 rounded border border-slate-300 text-slate-600 hover:bg-slate-50"
              >
                + Add amount
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (s.subtype === "prose" || s.subtype === "pearl" || s.subtype === "tip" || s.subtype === "caution") {
    const isCallout = s.subtype !== "prose";
    return (
      <div className="space-y-3">
        {isCallout && (
          <div className="grid grid-cols-[minmax(0,180px)_minmax(0,1fr)] gap-3">
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500">Style</label>
              <Select value={s.subtype} onValueChange={(v) => onChange({ ...s, subtype: v })}>
                <SelectTrigger data-testid="callout-style-select"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pearl">Clinical Pearl</SelectItem>
                  <SelectItem value="tip">Tip</SelectItem>
                  <SelectItem value="caution">Caution</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500">
                Title <span className="text-slate-400 normal-case">(optional — defaults to the style name)</span>
              </label>
              <Input value={s.heading} onChange={e => onChange({ ...s, heading: e.target.value })}
                     placeholder="e.g. WHEN TO CALL THE FAMILY" />
            </div>
          </div>
        )}
        <div>
          {isCallout && <label className="text-xs uppercase tracking-widest text-slate-500 block mb-1">Body</label>}
          <RichTextEditor value={s.html} onChange={(html) => onChange({ ...s, html })}/>
        </div>
      </div>
    );
  }

  if (s.subtype === "summary_321") {
    const d = s.data || {};
    const insights = Array.isArray(d.insights) ? d.insights : ["", "", ""];
    const actions  = Array.isArray(d.actions)  ? d.actions  : ["", ""];
    const question = d.question || "";
    const updList = (key, i, val) => {
      const list = [...(d[key] || [])];
      list[i] = val;
      onChange(setData(s, { [key]: list }));
    };
    const addItem = (key) => onChange(setData(s, { [key]: [...(d[key] || []), ""] }));
    const rmItem = (key, i) => {
      const list = [...(d[key] || [])];
      list.splice(i, 1);
      onChange(setData(s, { [key]: list }));
    };
    const paletteOptions = [
      { value: "sky", label: "Sky" },
      { value: "emerald", label: "Emerald" },
      { value: "amber", label: "Amber" },
      { value: "rose", label: "Rose" },
      { value: "violet", label: "Violet" },
      { value: "slate", label: "Slate" },
    ];
    return (
      <div className="space-y-4" data-testid="summary-321-editor">
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500">
              Heading <span className="text-slate-400 normal-case">(defaults to "3-2-1 Summary")</span>
            </label>
            <Input value={s.heading || ""} onChange={e => onChange({ ...s, heading: e.target.value })}
                   placeholder="3-2-1 Summary"/>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500">Columns</label>
            <Select value={String(d.columns ?? 3)} onValueChange={(v) => onChange(setData(s, { columns: parseInt(v, 10) }))}>
              <SelectTrigger data-testid="summary-columns-select"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1 — stacked</SelectItem>
                <SelectItem value="2">2 — side by side</SelectItem>
                <SelectItem value="3">3 — horizontal row</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <input type="checkbox" data-testid="summary-show-number"
                 checked={d.show_number !== false}
                 onChange={e => onChange(setData(s, { show_number: e.target.checked }))}
                 className="rounded border-slate-300"/>
          <label>Show counts in card titles (e.g. "3 Insights" vs. "Insights")</label>
        </div>
        <div className="grid md:grid-cols-3 gap-3">
          <div className="rounded-md border border-sky-100 bg-sky-50/50 p-3 space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-slate-500">Label</label>
                <Input value={d.label_insights || "Insight"} data-testid="summary-label-insights"
                       onChange={e => onChange(setData(s, { label_insights: e.target.value }))}
                       placeholder="Insight"/>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-slate-500">Colour</label>
                <Select value={d.color_insights || "sky"} onValueChange={(v) => onChange(setData(s, { color_insights: v }))}>
                  <SelectTrigger data-testid="summary-color-insights"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    {paletteOptions.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <label className="text-xs uppercase tracking-widest text-slate-500">Entries</label>
              <button type="button" onClick={() => addItem("insights")}
                      className="text-xs text-sky-700 hover:text-sky-900" data-testid="summary-add-insight">+ Add</button>
            </div>
            <div className="space-y-2">
              {insights.map((it, i) => (
                <div key={i} className="flex gap-1 items-start">
                  <textarea value={it} rows={2}
                            data-testid={`summary-insight-${i}`}
                            onChange={e => updList("insights", i, e.target.value)}
                            className="flex-1 text-sm border border-sky-200 rounded p-2 bg-white"
                            placeholder={`Entry #${i+1}`}/>
                  <button type="button" onClick={() => rmItem("insights", i)}
                          className="text-sky-400 hover:text-red-600 text-xs px-1"
                          title="Remove">×</button>
                </div>
              ))}
              {insights.length === 0 && <p className="text-xs italic text-slate-400">No entries yet.</p>}
            </div>
          </div>
          <div className="rounded-md border border-emerald-100 bg-emerald-50/50 p-3 space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-slate-500">Label</label>
                <Input value={d.label_actions || "Action"} data-testid="summary-label-actions"
                       onChange={e => onChange(setData(s, { label_actions: e.target.value }))}
                       placeholder="Action"/>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-slate-500">Colour</label>
                <Select value={d.color_actions || "emerald"} onValueChange={(v) => onChange(setData(s, { color_actions: v }))}>
                  <SelectTrigger data-testid="summary-color-actions"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    {paletteOptions.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <label className="text-xs uppercase tracking-widest text-slate-500">Entries</label>
              <button type="button" onClick={() => addItem("actions")}
                      className="text-xs text-emerald-700 hover:text-emerald-900" data-testid="summary-add-action">+ Add</button>
            </div>
            <div className="space-y-2">
              {actions.map((it, i) => (
                <div key={i} className="flex gap-1 items-start">
                  <textarea value={it} rows={2}
                            data-testid={`summary-action-${i}`}
                            onChange={e => updList("actions", i, e.target.value)}
                            className="flex-1 text-sm border border-emerald-200 rounded p-2 bg-white"
                            placeholder={`Entry #${i+1}`}/>
                  <button type="button" onClick={() => rmItem("actions", i)}
                          className="text-emerald-400 hover:text-red-600 text-xs px-1"
                          title="Remove">×</button>
                </div>
              ))}
              {actions.length === 0 && <p className="text-xs italic text-slate-400">No entries yet.</p>}
            </div>
          </div>
          <div className="rounded-md border border-amber-100 bg-amber-50/50 p-3 space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-slate-500">Label</label>
                <Input value={d.label_question || "Question"} data-testid="summary-label-question"
                       onChange={e => onChange(setData(s, { label_question: e.target.value }))}
                       placeholder="Question"/>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-slate-500">Colour</label>
                <Select value={d.color_question || "amber"} onValueChange={(v) => onChange(setData(s, { color_question: v }))}>
                  <SelectTrigger data-testid="summary-color-question"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    {paletteOptions.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <label className="text-xs uppercase tracking-widest text-slate-500 block mt-1">Question</label>
            <textarea value={question} rows={4}
                      data-testid="summary-question"
                      onChange={e => onChange(setData(s, { question: e.target.value }))}
                      className="w-full text-sm border border-amber-200 rounded p-2 bg-white italic"
                      placeholder="e.g. What would make today a good day?"/>
          </div>
        </div>
      </div>
    );
  }

  if (s.subtype === "bibliography") {
    const items = Array.isArray(s.data?.items) ? s.data.items : [];
    const update = (i, val) => {
      const list = [...items];
      list[i] = val;
      onChange(setData(s, { items: list }));
    };
    const add = () => onChange(setData(s, { items: [...items, ""] }));
    const rm = (i) => {
      const list = [...items];
      list.splice(i, 1);
      onChange(setData(s, { items: list }));
    };
    return (
      <div className="space-y-3" data-testid="bibliography-editor">
        <div>
          <label className="text-xs uppercase tracking-widest text-slate-500">
            Heading <span className="text-slate-400 normal-case">(defaults to "Bibliography")</span>
          </label>
          <Input value={s.heading || ""} onChange={e => onChange({ ...s, heading: e.target.value })}
                 placeholder="Bibliography"/>
        </div>
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs uppercase tracking-widest text-slate-500">
              Entries <span className="text-slate-400 normal-case">({items.length})</span>
            </label>
            <button type="button" onClick={add}
                    className="text-xs text-sky-700 hover:text-sky-900"
                    data-testid="bibliography-add-entry">+ Add entry</button>
          </div>
          <ol className="space-y-2 list-decimal pl-6">
            {items.map((entry, i) => (
              <li key={i} className="flex gap-1 items-start">
                <textarea value={entry} rows={2}
                          data-testid={`bibliography-entry-${i}`}
                          onChange={e => update(i, e.target.value)}
                          className="flex-1 text-sm border border-slate-200 rounded p-2 bg-white"
                          placeholder="Author, A. (Year). Title. Publisher."/>
                <button type="button" onClick={() => rm(i)}
                        className="text-slate-400 hover:text-red-600 text-xs px-1"
                        title="Remove">×</button>
              </li>
            ))}
            {items.length === 0 && <p className="text-xs italic text-slate-400">No entries yet. Click "Add entry" to start.</p>}
          </ol>
        </div>
      </div>
    );
  }

  if (s.subtype === "image") {
    const uploadOne = async (files) => {
      try {
        const data = await uploadImage(files[0]);
        onChange(setData(s, { image_url: data.url, alt: d.alt || data.alt || "", caption: d.caption || "" }));
        toast.success("Image uploaded");
      } catch (e) {
        toast.error(e.response?.data?.detail || e.message || "Upload failed");
      }
    };
    return (
      <div className="space-y-3">
        <DropZone testId="sub-image-dropzone" onFiles={uploadOne} className="rounded-md">
          <div className="border border-dashed border-slate-300 rounded-md p-4 bg-slate-50/50">
            {d.image_url ? (
              <img src={d.image_url} alt={d.alt || ""} className="max-h-48 mx-auto rounded object-contain"/>
            ) : (
              <div className="text-center py-8 flex flex-col items-center gap-2 text-slate-400">
                <UploadCloud className="w-8 h-8"/>
                <div className="text-sm">Drop an image here or</div>
                <label className="inline-flex items-center gap-1 text-xs text-sky-700 hover:text-sky-900 cursor-pointer border border-sky-300 rounded px-2 py-1">
                  Choose file
                  <input type="file" accept="image/*" className="sr-only"
                         onChange={e => e.target.files?.[0] && uploadOne([e.target.files[0]])}/>
                </label>
              </div>
            )}
            {d.image_url && (
              <div className="mt-2 text-center">
                <label className="text-xs text-sky-700 hover:text-sky-900 cursor-pointer">
                  Replace
                  <input type="file" accept="image/*" className="sr-only"
                         onChange={e => e.target.files?.[0] && uploadOne([e.target.files[0]])}/>
                </label>
              </div>
            )}
          </div>
        </DropZone>
        <div>
          <label className="text-xs uppercase tracking-widest text-slate-500">Image URL</label>
          <Input data-testid="sub-image-url" value={d.image_url || ""} placeholder="/api/files/… or https://…"
                 onChange={e => onChange(setData(s, { image_url: e.target.value }))}/>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-slate-500">Alt text</label>
          <Input value={d.alt || ""} onChange={e => onChange(setData(s, { alt: e.target.value }))}/>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-slate-500">Caption</label>
          <Input value={d.caption || ""} onChange={e => onChange(setData(s, { caption: e.target.value }))}/>
        </div>
        <ImageStyleFields d={d} patch={p => onChange(setData(s, p))}/>
      </div>
    );
  }

  if (s.subtype === "image_and_text") {
    const uploadOne = async (files) => {
      try {
        const data = await uploadImage(files[0]);
        onChange(setData(s, { image_url: data.url, alt: d.alt || data.alt || "" }));
        toast.success("Image uploaded");
      } catch (e) {
        toast.error(e.response?.data?.detail || e.message || "Upload failed");
      }
    };
    return (
      <div className="space-y-3">
        <DropZone testId="sub-image-and-text-dropzone" onFiles={uploadOne} className="rounded-md">
          <div className="border border-dashed border-slate-300 rounded-md p-3 bg-slate-50/50">
            {d.image_url ? (
              <img src={d.image_url} alt={d.alt || ""} className="max-h-32 mx-auto rounded object-contain"/>
            ) : (
              <div className="text-center py-4 flex flex-col items-center gap-1 text-slate-400">
                <UploadCloud className="w-6 h-6"/>
                <div className="text-xs">Drop an image here or</div>
                <label className="inline-flex items-center gap-1 text-xs text-sky-700 hover:text-sky-900 cursor-pointer border border-sky-300 rounded px-2 py-0.5">
                  Choose file
                  <input type="file" accept="image/*" className="sr-only"
                         onChange={e => e.target.files?.[0] && uploadOne([e.target.files[0]])}/>
                </label>
              </div>
            )}
          </div>
        </DropZone>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500">Image URL</label>
            <Input value={d.image_url || ""} onChange={e => onChange(setData(s, { image_url: e.target.value }))}/>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500">Image position</label>
            <Select value={d.image_position || "left"} onValueChange={v => onChange(setData(s, { image_position: v }))}>
              <SelectTrigger><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="left">Left</SelectItem>
                <SelectItem value="right">Right</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-slate-500">Alt / Caption</label>
          <Input placeholder="Alt text" value={d.alt || ""} onChange={e => onChange(setData(s, { alt: e.target.value }))} className="mb-2"/>
          <Input placeholder="Caption" value={d.caption || ""} onChange={e => onChange(setData(s, { caption: e.target.value }))}/>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-slate-500">Text</label>
          <RichTextEditor value={s.html} onChange={(html) => onChange({ ...s, html })}/>
        </div>
        <ImageStyleFields d={d} patch={p => onChange(setData(s, p))}/>
      </div>
    );
  }

  if (s.subtype === "image_gallery") {
    const images = d.images || [];
    const update = (idx, patch) => {
      const arr = [...images]; arr[idx] = { ...arr[idx], ...patch };
      onChange(setData(s, { images: arr }));
    };
    const add = () => onChange(setData(s, { images: [...images, { url: "", alt: "", caption: "" }] }));
    const del = (idx) => confirm("Delete image?", "This image will be removed from the gallery.", () =>
      onChange(setData(s, { images: images.filter((_, i) => i !== idx) })));
    const uploadMany = async (files) => {
      try {
        const results = await Promise.all(files.map(uploadImage));
        const additions = results.map(r => ({ url: r.url, alt: r.alt || "", caption: "" }));
        onChange(setData(s, { images: [...images, ...additions] }));
        toast.success(`${results.length} image${results.length === 1 ? "" : "s"} uploaded`);
      } catch (e) {
        toast.error(e.response?.data?.detail || e.message || "Upload failed");
      }
    };
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-xs uppercase tracking-widest text-slate-500">Gallery images ({images.length})</div>
          <div className="flex items-center gap-3">
            <label className="text-xs text-sky-700 hover:text-sky-900 inline-flex items-center gap-1 cursor-pointer">
              <UploadCloud className="w-3.5 h-3.5"/>Upload
              <input type="file" accept="image/*" multiple className="sr-only"
                     onChange={e => e.target.files?.length && uploadMany(Array.from(e.target.files))}/>
            </label>
            <button data-testid="gallery-add-image" onClick={add} className="text-xs text-sky-700 hover:text-sky-900 inline-flex items-center gap-1">
              <Plus className="w-3.5 h-3.5"/>Add empty
            </button>
          </div>
        </div>
        <DropZone testId="gallery-dropzone" multiple onFiles={uploadMany} className="rounded-md">
          <div className="space-y-2 border border-dashed border-slate-300 rounded-md p-2 bg-slate-50/50 min-h-[80px]">
            {images.map((im, i) => (
              <div key={i} className="grid grid-cols-[80px_minmax(0,1fr)_auto] gap-2 items-center border border-slate-200 rounded-md p-2 bg-white">
                {im.url
                  ? <img src={im.url} alt="" className="w-20 h-16 object-cover rounded"/>
                  : <div className="w-20 h-16 bg-slate-100 rounded grid place-items-center text-slate-400 text-xs">No URL</div>}
                <div className="space-y-1">
                  <Input placeholder="Image URL" value={im.url} onChange={e => update(i, { url: e.target.value })}/>
                  <div className="grid grid-cols-2 gap-2">
                    <Input placeholder="Alt" value={im.alt || ""} onChange={e => update(i, { alt: e.target.value })}/>
                    <Input placeholder="Caption" value={im.caption || ""} onChange={e => update(i, { caption: e.target.value })}/>
                  </div>
                </div>
                <button onClick={() => del(i)} className="text-red-500 hover:text-red-700 p-1" aria-label="Delete image">
                  <Trash2 className="w-4 h-4"/>
                </button>
              </div>
            ))}
            {images.length === 0 && (
              <div className="text-center py-6 text-slate-400 flex flex-col items-center gap-1">
                <UploadCloud className="w-6 h-6"/>
                <div className="text-sm italic">Drop images here to add them all at once.</div>
              </div>
            )}
          </div>
        </DropZone>
      </div>
    );
  }

  if (s.subtype === "table") {
    const cols = d.columns || [];
    const rows = d.rows || [];
    const setCol = (i, val) => { const c = [...cols]; c[i] = val; onChange(setData(s, { columns: c })); };
    const setCell = (ri, ci, val) => {
      const r = rows.map(row => [...row]);
      if (!r[ri]) r[ri] = [];
      r[ri][ci] = val;
      onChange(setData(s, { rows: r }));
    };
    const addRow = () => onChange(setData(s, { rows: [...rows, cols.map(() => "")] }));
    const delRow = (i) => confirm("Delete row?", "This row and its data will be removed.", () =>
      onChange(setData(s, { rows: rows.filter((_, x) => x !== i) })));
    const addCol = () => onChange(setData(s, {
      columns: [...cols, `Column ${cols.length + 1}`],
      rows: rows.map(r => [...r, ""]),
    }));
    const delCol = (i) => confirm("Delete column?", `Column "${cols[i]}" and all its data will be removed.`, () =>
      onChange(setData(s, {
        columns: cols.filter((_, x) => x !== i),
        rows: rows.map(r => r.filter((_, x) => x !== i)),
      })));
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <div className="text-xs uppercase tracking-widest text-slate-500 flex-1">Table ({cols.length}×{rows.length})</div>
          <button onClick={addCol} className="text-xs text-sky-700 hover:text-sky-900">+ Column</button>
          <button onClick={addRow} className="text-xs text-sky-700 hover:text-sky-900">+ Row</button>
        </div>
        <div className="overflow-x-auto border border-slate-200 rounded">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                {cols.map((c, i) => (
                  <th key={i} className="p-1 border-b border-slate-200">
                    <div className="flex items-center gap-1">
                      <Input value={c} onChange={e => setCol(i, e.target.value)} className="h-7 text-xs font-semibold"/>
                      <button onClick={() => delCol(i)} className="text-red-400 hover:text-red-700 p-0.5" aria-label="Delete column">
                        <Trash2 className="w-3 h-3"/>
                      </button>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, ri) => (
                <tr key={ri}>
                  {cols.map((_, ci) => (
                    <td key={ci} className="p-1 border-b border-slate-200">
                      <Input value={r[ci] ?? ""} onChange={e => setCell(ri, ci, e.target.value)} className="h-7 text-xs"/>
                    </td>
                  ))}
                  <td className="p-1 border-b border-slate-200 w-8">
                    <button onClick={() => delRow(ri)} className="text-red-400 hover:text-red-700" aria-label="Delete row">
                      <Trash2 className="w-3 h-3"/>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-slate-500">Caption</label>
          <Input value={d.caption || ""} onChange={e => onChange(setData(s, { caption: e.target.value }))}/>
        </div>
      </div>
    );
  }

  if (s.subtype === "graph") {
    const series = d.series || [];
    const update = (i, patch) => {
      const arr = [...series]; arr[i] = { ...arr[i], ...patch };
      onChange(setData(s, { series: arr }));
    };
    const add = () => onChange(setData(s, { series: [...series, { label: "", value: 0 }] }));
    const del = (i) => confirm("Delete data point?", `"${series[i]?.label || "Unnamed"}" will be removed from the chart.`, () =>
      onChange(setData(s, { series: series.filter((_, x) => x !== i) })));
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <label className="text-xs uppercase tracking-widest text-slate-500">Chart type</label>
          <Select value={d.chart_type || "bar"} onValueChange={v => onChange(setData(s, { chart_type: v }))}>
            <SelectTrigger className="w-40 h-8"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="bar">Bar</SelectItem>
              <SelectItem value="line">Line</SelectItem>
              <SelectItem value="pie">Pie</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex-1"/>
          <button onClick={add} className="text-xs text-sky-700 hover:text-sky-900 inline-flex items-center gap-1">
            <Plus className="w-3.5 h-3.5"/>Add point
          </button>
        </div>
        <div className="space-y-1">
          {series.map((pt, i) => (
            <div key={i} className="grid grid-cols-[minmax(0,1fr)_120px_32px] gap-2 items-center">
              <Input value={pt.label} placeholder="Label" onChange={e => update(i, { label: e.target.value })} className="h-8 text-sm"/>
              <Input type="number" value={pt.value} onChange={e => update(i, { value: Number(e.target.value) })} className="h-8 text-sm"/>
              <button onClick={() => del(i)} className="text-red-500 hover:text-red-700" aria-label="Delete data point">
                <Trash2 className="w-4 h-4"/>
              </button>
            </div>
          ))}
          {series.length === 0 && <div className="text-sm italic text-slate-400">No data points yet.</div>}
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-slate-500">Caption</label>
          <Input value={d.caption || ""} onChange={e => onChange(setData(s, { caption: e.target.value }))}/>
        </div>
      </div>
    );
  }

  if (s.subtype === "divider") {
    return (
      <div className="space-y-3">
        <div className="text-xs uppercase tracking-widest text-sky-700 font-semibold">Page divider</div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500">Line style</label>
            <Select value={d.style || "solid"} onValueChange={v => onChange(setData(s, { style: v }))}>
              <SelectTrigger><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="solid">Solid</SelectItem>
                <SelectItem value="dashed">Dashed</SelectItem>
                <SelectItem value="dotted">Dotted</SelectItem>
                <SelectItem value="double">Double</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500">Thickness</label>
            <Select value={d.thickness || "thin"} onValueChange={v => onChange(setData(s, { thickness: v }))}>
              <SelectTrigger><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="thin">Thin (1px)</SelectItem>
                <SelectItem value="medium">Medium (2px)</SelectItem>
                <SelectItem value="thick">Thick (4px)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500">Width</label>
            <Select value={d.width || "full"} onValueChange={v => onChange(setData(s, { width: v }))}>
              <SelectTrigger><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="full">Full width</SelectItem>
                <SelectItem value="wide">Wide (75%)</SelectItem>
                <SelectItem value="narrow">Narrow (50%)</SelectItem>
                <SelectItem value="tight">Tight (25%)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500">Alignment</label>
            <Select value={d.align || "center"} onValueChange={v => onChange(setData(s, { align: v }))}>
              <SelectTrigger><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="left">Left</SelectItem>
                <SelectItem value="center">Center</SelectItem>
                <SelectItem value="right">Right</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <label className="text-xs uppercase tracking-widest text-slate-500">Vertical spacing</label>
            <Select value={d.margin || "md"} onValueChange={v => onChange(setData(s, { margin: v }))}>
              <SelectTrigger><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="sm">Small</SelectItem>
                <SelectItem value="md">Medium</SelectItem>
                <SelectItem value="lg">Large</SelectItem>
                <SelectItem value="xl">Extra large</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <label className="text-xs uppercase tracking-widest text-slate-500">Color</label>
            <div className="flex gap-2 items-center">
              <input type="color" value={d.color || "#e2e8f0"}
                     onChange={e => onChange(setData(s, { color: e.target.value }))}
                     className="w-10 h-9 rounded border border-slate-300 cursor-pointer"/>
              <Input value={d.color || ""} placeholder="#e2e8f0"
                     onChange={e => onChange(setData(s, { color: e.target.value }))}/>
            </div>
          </div>
          <div className="col-span-2">
            <label className="text-xs uppercase tracking-widest text-slate-500">Center label (optional)</label>
            <Input value={d.label || ""} placeholder="e.g. * * * or CHAPTER 2"
                   onChange={e => onChange(setData(s, { label: e.target.value }))}/>
            <p className="text-xs text-slate-500 mt-1">Leaving this blank renders a plain line. Text appears centered between two half-lines.</p>
          </div>
        </div>
      </div>
    );
  }


  if (s.subtype === "hero") {
    const uploadHero = async (files) => {
      try {
        const data = await uploadImage(files[0]);
        onChange(setData(s, { image_url: data.url, image_alt: d.image_alt || data.alt || "" }));
        toast.success("Image uploaded");
      } catch (e) {
        toast.error(e.response?.data?.detail || e.message || "Upload failed");
      }
    };
    const layout = d.layout || "image-right";
    const showImage = layout !== "text-only";
    const showText = layout !== "image-only";
    return (
      <div className="space-y-3">
        <div className="text-xs uppercase tracking-widest text-sky-700 font-semibold">Hero — big introduction block</div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500">Layout</label>
            <Select value={layout} onValueChange={(v) => onChange(setData(s, { layout: v }))}>
              <SelectTrigger data-testid="hero-layout-select"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="image-right">Text left · image right</SelectItem>
                <SelectItem value="image-left">Image left · text right</SelectItem>
                <SelectItem value="image-top">Image on top</SelectItem>
                <SelectItem value="image-bottom">Image on bottom</SelectItem>
                <SelectItem value="centered">Centered — text then image</SelectItem>
                <SelectItem value="text-only">Text only</SelectItem>
                <SelectItem value="image-only">Image only</SelectItem>
                <SelectItem value="overlay">Overlay — text on image</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500">Text align</label>
            <Select value={d.text_align || "left"} onValueChange={(v) => onChange(setData(s, { text_align: v }))}>
              <SelectTrigger><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="left">Left</SelectItem>
                <SelectItem value="center">Center</SelectItem>
                <SelectItem value="right">Right</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        {showText && (
          <>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500">Eyebrow (small label)</label>
              <Input value={d.eyebrow || ""} onChange={e => onChange(setData(s, { eyebrow: e.target.value }))}/>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500">Title</label>
              <Input value={d.title || ""} onChange={e => onChange(setData(s, { title: e.target.value }))}/>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500">Description — rich text (overrides plain text below when set)</label>
              <RichTextEditor value={d.description_html || ""} onChange={(html) => onChange(setData(s, { description_html: html }))}/>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500">Plain description (fallback if rich text is empty)</label>
              <Textarea rows={3} value={d.description || ""} onChange={e => onChange(setData(s, { description: e.target.value }))}/>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <ThemedColorField label="Title color (optional)"
                  value={d.title_color} valueDark={d.title_color_dark}
                  onChange={(v) => onChange(setData(s, { title_color: v }))}
                  onChangeDark={(v) => onChange(setData(s, { title_color_dark: v }))}
                  defaultColor="#0f172a"/>
              </div>
              <div className="col-span-2">
                <ThemedColorField label="Description color (optional)"
                  value={d.subtext_color} valueDark={d.subtext_color_dark}
                  onChange={(v) => onChange(setData(s, { subtext_color: v }))}
                  onChangeDark={(v) => onChange(setData(s, { subtext_color_dark: v }))}
                  defaultColor="#475569"/>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500">Primary CTA — label</label>
                <Input value={d.cta_label || ""} onChange={e => onChange(setData(s, { cta_label: e.target.value }))}/>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500">Primary CTA — href</label>
                <Input value={d.cta_href || ""} onChange={e => onChange(setData(s, { cta_href: e.target.value }))} placeholder="/pathways"/>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500">Primary CTA — style</label>
                <Select value={d.cta_style || "primary"} onValueChange={(v) => onChange(setData(s, { cta_style: v }))}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="primary">Primary</SelectItem>
                    <SelectItem value="secondary">Secondary</SelectItem>
                    <SelectItem value="ghost">Ghost</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500">Secondary CTA — label (optional)</label>
                <Input value={d.cta2_label || ""} onChange={e => onChange(setData(s, { cta2_label: e.target.value }))}/>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500">Secondary CTA — href</label>
                <Input value={d.cta2_href || ""} onChange={e => onChange(setData(s, { cta2_href: e.target.value }))} placeholder="/"/>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500">Secondary CTA — style</label>
                <Select value={d.cta2_style || "ghost"} onValueChange={(v) => onChange(setData(s, { cta2_style: v }))}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="primary">Primary</SelectItem>
                    <SelectItem value="secondary">Secondary</SelectItem>
                    <SelectItem value="ghost">Ghost</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </>
        )}
        {showImage && (
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500">Hero image</label>
            <DropZone onFiles={uploadHero} className="rounded-md">
              <div className="border border-dashed border-slate-300 rounded-md p-3 bg-slate-50/50 flex items-center gap-3">
                {d.image_url ? (
                  <img src={d.image_url} alt={d.image_alt || ""} className="w-24 h-16 object-cover rounded shrink-0"/>
                ) : (
                  <div className="w-24 h-16 grid place-items-center text-slate-400 shrink-0"><UploadCloud className="w-5 h-5"/></div>
                )}
                <div className="flex-1 min-w-0">
                  <Input value={d.image_url || ""} placeholder="Drop an image or paste a URL"
                         onChange={e => onChange(setData(s, { image_url: e.target.value }))}/>
                  <div className="flex items-center gap-3 mt-1">
                    <label className="text-xs text-sky-700 hover:text-sky-900 cursor-pointer">
                      Choose file
                      <input type="file" accept="image/*" className="sr-only"
                             onChange={e => e.target.files?.[0] && uploadHero([e.target.files[0]])}/>
                    </label>
                    {d.image_url && (
                      <button onClick={() => onChange(setData(s, { image_url: "" }))} className="text-xs text-slate-400 hover:text-red-600">remove</button>
                    )}
                  </div>
                </div>
              </div>
            </DropZone>
            <label className="text-xs uppercase tracking-widest text-slate-500 mt-2 block">Image alt</label>
            <Input value={d.image_alt || ""} onChange={e => onChange(setData(s, { image_alt: e.target.value }))}/>
            <ImageStyleFields d={d} patch={p => onChange(setData(s, p))} label="Hero image style"/>
          </div>
        )}
        <DynamicSectionStyle d={d} onPatch={p => onChange(setData(s, p))}/>
      </div>
    );
  }

  if (s.subtype === "layout_grid") {
    return <LayoutGridEditor section={s} onChange={onChange}/>;
  }

  if (s.subtype === "pathways_grid") {
    return (
      <div className="space-y-3">
        <div className="text-xs uppercase tracking-widest text-sky-700 font-semibold">Pathways grid — auto-lists pathways</div>
        <p className="text-xs text-slate-500">Cards are populated from Admin → Content → Pathways automatically. Configure only the header here.</p>
        <div>
          <label className="text-xs uppercase tracking-widest text-slate-500">Eyebrow</label>
          <Input value={d.eyebrow || ""} onChange={e => onChange(setData(s, { eyebrow: e.target.value }))}/>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-slate-500">Heading</label>
          <Input value={d.heading || ""} onChange={e => onChange(setData(s, { heading: e.target.value }))}/>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-slate-500">Description</label>
          <Textarea rows={2} value={d.description || ""} onChange={e => onChange(setData(s, { description: e.target.value }))}/>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-slate-500">How many pathways to show (1–24)</label>
          <Input type="number" min={1} max={24} value={d.limit || 12}
                 onChange={e => onChange(setData(s, { limit: Math.max(1, Math.min(24, Number(e.target.value) || 12)) }))}/>
        </div>
        <DynamicSectionStyle d={d} onPatch={p => onChange(setData(s, p))}/>
      </div>
    );
  }

  if (s.subtype === "recent_blogs") {
    return (
      <div className="space-y-3">
        <div className="text-xs uppercase tracking-widest text-sky-700 font-semibold">Recent blogs — auto-lists newest blogs</div>
        <div>
          <label className="text-xs uppercase tracking-widest text-slate-500">Heading</label>
          <Input value={d.heading || ""} onChange={e => onChange(setData(s, { heading: e.target.value }))}/>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-slate-500">How many blogs (1–24)</label>
          <Input type="number" min={1} max={24} value={d.limit || 6}
                 onChange={e => onChange(setData(s, { limit: Math.max(1, Math.min(24, Number(e.target.value) || 6)) }))}/>
        </div>
        <DynamicSectionStyle d={d} onPatch={p => onChange(setData(s, p))}/>
      </div>
    );
  }

  if (s.subtype === "glossary_teaser") {
    return (
      <div className="space-y-3">
        <div className="text-xs uppercase tracking-widest text-sky-700 font-semibold">Glossary teaser — link to the glossary</div>
        <div>
          <label className="text-xs uppercase tracking-widest text-slate-500">Eyebrow</label>
          <Input value={d.eyebrow || ""} onChange={e => onChange(setData(s, { eyebrow: e.target.value }))}/>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-slate-500">Heading</label>
          <Input value={d.heading || ""} onChange={e => onChange(setData(s, { heading: e.target.value }))}/>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-slate-500">Description</label>
          <Textarea rows={2} value={d.description || ""} onChange={e => onChange(setData(s, { description: e.target.value }))}/>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500">CTA label</label>
            <Input value={d.cta_label || ""} onChange={e => onChange(setData(s, { cta_label: e.target.value }))}/>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500">CTA href</label>
            <Input value={d.cta_href || ""} onChange={e => onChange(setData(s, { cta_href: e.target.value }))} placeholder="/glossary"/>
          </div>
        </div>
        <DynamicSectionStyle d={d} onPatch={p => onChange(setData(s, p))}/>
      </div>
    );
  }

  return <Textarea rows={6} value={s.html} onChange={e => onChange({ ...s, html: e.target.value })}/>;
}

// ============================================================================
// Layout Grid editor — preset selector + per-cell editor + custom mode.
// Cells are reorderable via native HTML5 drag-and-drop (kept native so it
// doesn't conflict with the outer @hello-pangea/dnd context used by the
// section list itself).
// ============================================================================
function LayoutGridEditor({ section, onChange }) {
  const s = section;
  const d = s.data || {};
  const preset = d.preset || "2-col-split";
  const isCustom = preset === "custom";
  const cells = Array.isArray(d.cells) ? d.cells : [];
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  // Ref mirrors dragIdx so drag event handlers can read the source index
  // synchronously — React state closures don't update within a single drag
  // gesture (the same handler instance is fired for dragstart→dragover→drop).
  const dragIdxRef = React.useRef(null);
  const patchData = (patch) => onChange({ ...s, data: { ...(s.data || {}), ...patch } });
  const patchCell = (idx, patch) => {
    const next = cells.slice();
    next[idx] = { ...next[idx], ...patch };
    patchData({ cells: next });
  };
  const removeCell = (idx) => {
    const next = cells.slice();
    next.splice(idx, 1);
    patchData({ cells: next });
  };
  const moveCell = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= cells.length) return;
    const next = cells.slice();
    [next[idx], next[j]] = [next[j], next[idx]];
    patchData({ cells: next });
  };
  const reorderCells = (from, to) => {
    if (from == null || to == null || from === to) return;
    const next = cells.slice();
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    patchData({ cells: next });
  };
  const addCell = (content_type) => {
    patchData({ cells: [...cells, defaultLayoutGridCell(content_type)] });
  };
  const applyPreset = (nextPreset) => {
    if (nextPreset === "custom") {
      // Keep existing cells, just switch mode.
      patchData({ preset: nextPreset });
    } else {
      patchData({ preset: nextPreset, cells: makeDefaultCells(nextPreset) });
    }
  };

  const cols = isCustom ? Number(d.columns) || 2 : (LAYOUT_GRID_PRESETS[preset]?.columns || 2);

  return (
    <div className="space-y-3">
      <div className="text-xs uppercase tracking-widest text-sky-700 font-semibold">Custom grid — box layout</div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="text-xs uppercase tracking-widest text-slate-500">Preset</label>
          <Select value={preset} onValueChange={applyPreset}>
            <SelectTrigger data-testid="grid-preset-select"><SelectValue/></SelectTrigger>
            <SelectContent>
              {Object.entries(LAYOUT_GRID_PRESETS).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {isCustom && (
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500">Columns</label>
            <Select value={String(cols)} onValueChange={(v) => patchData({ columns: Number(v) })}>
              <SelectTrigger><SelectValue/></SelectTrigger>
              <SelectContent>
                {[1, 2, 3, 4, 6].map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <div>
          <label className="text-xs uppercase tracking-widest text-slate-500">Gap</label>
          <Select value={d.gap || "md"} onValueChange={(v) => patchData({ gap: v })}>
            <SelectTrigger><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="sm">Small</SelectItem>
              <SelectItem value="md">Medium</SelectItem>
              <SelectItem value="lg">Large</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="border-t border-slate-200 pt-3">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs uppercase tracking-widest text-slate-500">Cells ({cells.length}) — {cols} column{cols === 1 ? "" : "s"}</div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => addCell("image")}
                    data-testid="grid-add-image"
                    className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-slate-300 hover:border-sky-500 hover:text-sky-700">
              <ImageIcon className="w-3.5 h-3.5"/>Add image
            </button>
            <button type="button" onClick={() => addCell("text")}
                    data-testid="grid-add-text"
                    className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-slate-300 hover:border-sky-500 hover:text-sky-700">
              <TypeIcon className="w-3.5 h-3.5"/>Add text
            </button>
            <button type="button" onClick={() => addCell("button")}
                    data-testid="grid-add-button"
                    className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-slate-300 hover:border-sky-500 hover:text-sky-700">
              <MousePointerClick className="w-3.5 h-3.5"/>Add button
            </button>
          </div>
        </div>
        {cells.length === 0 ? (
          <div className="border border-dashed border-slate-300 rounded p-6 text-center text-slate-500 text-sm">
            No cells yet. Add an image, text block, or button above.
          </div>
        ) : (
          <ul className="space-y-2"
              onDragLeave={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget)) setOverIdx(null);
              }}>
            {cells.map((c, idx) => {
              const isDragging = dragIdx === idx;
              const isDropTarget = overIdx === idx && dragIdx !== null && dragIdx !== idx;
              return (
              <li key={c.id} data-testid={`grid-cell-${idx}`}
                  onDragOver={(e) => {
                    if (dragIdxRef.current === null) return;
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                    if (overIdx !== idx) setOverIdx(idx);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    reorderCells(dragIdxRef.current, idx);
                    dragIdxRef.current = null;
                    setDragIdx(null);
                    setOverIdx(null);
                  }}
                  className={`border border-slate-200 rounded-md p-3 bg-slate-50/40 transition-all
                    ${isDragging ? "opacity-40 scale-[0.99]" : ""}
                    ${isDropTarget ? "ring-2 ring-sky-400 ring-offset-1" : ""}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span draggable
                        onDragStart={(e) => {
                          dragIdxRef.current = idx;
                          setDragIdx(idx);
                          e.dataTransfer.effectAllowed = "move";
                          try { e.dataTransfer.setData("text/plain", String(idx)); } catch {}
                        }}
                        onDragEnd={() => {
                          dragIdxRef.current = null;
                          setDragIdx(null);
                          setOverIdx(null);
                        }}
                        data-testid={`grid-cell-handle-${idx}`}
                        className="text-slate-400 hover:text-slate-700 cursor-grab active:cursor-grabbing select-none"
                        title="Drag to reorder">
                    <GripVertical className="w-4 h-4"/>
                  </span>
                  <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-slate-200 text-slate-700">{c.content_type}</span>
                  <span className="text-xs text-slate-500">#{idx + 1} · span {c.span || 1}</span>
                  <div className="flex-1"/>
                  <button type="button" onClick={() => moveCell(idx, -1)} disabled={idx === 0} className="p-1 disabled:opacity-30 hover:text-sky-700" title="Move up"><ArrowUp className="w-4 h-4"/></button>
                  <button type="button" onClick={() => moveCell(idx, 1)} disabled={idx === cells.length - 1} className="p-1 disabled:opacity-30 hover:text-sky-700" title="Move down"><ArrowDown className="w-4 h-4"/></button>
                  <button type="button" onClick={() => removeCell(idx)} className="p-1 text-slate-400 hover:text-red-600" title="Remove"><Trash2 className="w-4 h-4"/></button>
                </div>
                <GridCellFields c={c} maxCols={cols} onChange={(patch) => patchCell(idx, patch)}/>
              </li>
              );
            })}
          </ul>
        )}
      </div>
      <DynamicSectionStyle d={d} onPatch={patchData}/>
    </div>
  );
}

function GridCellFields({ c, maxCols, onChange }) {
  const doUpload = async (files) => {
    try {
      const data = await uploadImage(files[0]);
      onChange({ image_url: data.url, image_alt: c.image_alt || data.alt || "" });
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Upload failed");
    }
  };
  return (
    <div className="space-y-2 pl-6">
      <div className="grid grid-cols-4 gap-2">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-slate-500">Span</label>
          <Select value={String(c.span || 1)} onValueChange={(v) => onChange({ span: Number(v) })}>
            <SelectTrigger className="h-8"><SelectValue/></SelectTrigger>
            <SelectContent>
              {Array.from({ length: maxCols }, (_, i) => i + 1).map(n => (
                <SelectItem key={n} value={String(n)}>{n}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-slate-500">Padding</label>
          <Select value={c.padding || "md"} onValueChange={(v) => onChange({ padding: v })}>
            <SelectTrigger className="h-8"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              <SelectItem value="sm">Small</SelectItem>
              <SelectItem value="md">Medium</SelectItem>
              <SelectItem value="lg">Large</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-slate-500">Corners</label>
          <Select value={c.radius || "sm"} onValueChange={(v) => onChange({ radius: v })}>
            <SelectTrigger className="h-8"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Sharp</SelectItem>
              <SelectItem value="sm">Small</SelectItem>
              <SelectItem value="md">Medium</SelectItem>
              <SelectItem value="lg">Large</SelectItem>
              <SelectItem value="xl">Extra</SelectItem>
              <SelectItem value="full">Round</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-slate-500">Align</label>
          <Select value={c.text_align || "left"} onValueChange={(v) => onChange({ text_align: v })}>
            <SelectTrigger className="h-8"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="left">Left</SelectItem>
              <SelectItem value="center">Center</SelectItem>
              <SelectItem value="right">Right</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <ThemedColorField label="Background color (optional)"
        value={c.bg_color} valueDark={c.bg_color_dark}
        onChange={(v) => onChange({ bg_color: v })}
        onChangeDark={(v) => onChange({ bg_color_dark: v })}
        defaultColor="#ffffff"/>
      {c.content_type === "image" && (
        <>
          <DropZone onFiles={doUpload} className="rounded-md">
            <div className="border border-dashed border-slate-300 rounded p-2 bg-white flex items-center gap-2">
              {c.image_url ? (
                <img src={c.image_url} alt="" className="w-20 h-14 object-cover rounded shrink-0"/>
              ) : (
                <div className="w-20 h-14 grid place-items-center text-slate-400 shrink-0"><UploadCloud className="w-4 h-4"/></div>
              )}
              <div className="flex-1 min-w-0">
                <Input value={c.image_url || ""} placeholder="Drop image or paste URL" className="h-8"
                       onChange={(e) => onChange({ image_url: e.target.value })}/>
                <label className="text-xs text-sky-700 hover:text-sky-900 cursor-pointer">
                  Choose file
                  <input type="file" accept="image/*" className="sr-only"
                         onChange={e => e.target.files?.[0] && doUpload([e.target.files[0]])}/>
                </label>
              </div>
            </div>
          </DropZone>
          <Input value={c.image_alt || ""} placeholder="Alt text" className="h-8"
                 onChange={(e) => onChange({ image_alt: e.target.value })}/>
          <Input value={c.link_href || ""} placeholder="Link href (optional — wraps image in a link)" className="h-8"
                 onChange={(e) => onChange({ link_href: e.target.value })}/>
        </>
      )}
      {c.content_type === "text" && (
        <>
          <Input value={c.heading || ""} placeholder="Heading (optional)" className="h-8"
                 onChange={(e) => onChange({ heading: e.target.value })}/>
          <RichTextEditor value={c.html || "<p></p>"} onChange={(html) => onChange({ html })}/>
        </>
      )}
      {c.content_type === "button" && (
        <div className="grid grid-cols-3 gap-2">
          <Input value={c.label || ""} placeholder="Label" className="h-8"
                 onChange={(e) => onChange({ label: e.target.value })}/>
          <Input value={c.href || ""} placeholder="Href e.g. /pathways" className="h-8"
                 onChange={(e) => onChange({ href: e.target.value })}/>
          <Select value={c.style || "primary"} onValueChange={(v) => onChange({ style: v })}>
            <SelectTrigger className="h-8"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="primary">Primary</SelectItem>
              <SelectItem value="secondary">Secondary</SelectItem>
              <SelectItem value="ghost">Ghost</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
}
